import {
  getOutcomeResultForValueId,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import type { SampleToolResultOutcomeGradingEntry } from "@repo/app-types/entities/playbookNodeSampleToolResult";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, useMemo } from "react";
import { GradingsTableContactCell } from "../../features/teammatePerformance/gradingsTableContactCell.js";
import { TeammateNameStack } from "../../features/teammatePerformance/teammateNameStack.js";

interface OutcomeSampleGradingsTableProps {
  entries: readonly SampleToolResultOutcomeGradingEntry[];
  outcomeDefinition: OutcomeDefinition;
  isLoading?: boolean;
  isError?: boolean;
}

type OutcomeSampleGradingRow = SampleToolResultOutcomeGradingEntry & {
  id: string;
};

export const OutcomeSampleGradingsTable: FC<OutcomeSampleGradingsTableProps> = (
  props,
) => {
  const { entries, outcomeDefinition } = props;

  const outcomeLabel = (grade: { value: string } | null | undefined): string =>
    grade
      ? (getOutcomeResultForValueId(outcomeDefinition, grade.value)?.label ??
        grade.value)
      : "—";

  const rows = useMemo(
    (): OutcomeSampleGradingRow[] =>
      entries.map((e) => ({ ...e, id: e.grading.id })),
    [entries],
  );

  const columns = useMemo(
    (): EntityTableColumnDefinition<OutcomeSampleGradingRow>[] => [
      {
        id: "customer",
        className: "w-[28%]",
        header: "customer",
        isRowHeader: true,
        renderCell: (entry) => (
          <GradingsTableContactCell grading={entry.grading} />
        ),
      },
      {
        id: "teammate",
        className: "w-[20%]",
        header: "Teammate",
        renderCell: (entry) => <TeammateNameStack teammate={entry.teammate} />,
      },
      {
        id: "currentGrade",
        className: "w-0",
        header: "Current",
        renderCell: (entry) => outcomeLabel(entry.baseline_grade),
      },
      {
        id: "testGrade",
        className: "w-0",
        header: "Test",
        renderCell: (entry) => outcomeLabel(entry.tested_grade),
      },
      {
        id: "testCommentary",
        className: "w-[50%]",
        header: "Test commentary",
        renderCell: (entry) => (
          <div>{entry.tested_grade?.commentary ?? "—"}</div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outcomeDefinition],
  );

  return (
    <EntityTable
      aria-label="Graded calls"
      columns={columns}
      entities={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      emptyMessage="No graded calls in this sample."
      size={Size.SMALL}
    />
  );
};
